/**
 * CreateNewPropertyForm renderer (iter303ag-fix7)
 *
 * Used inside the "No — different appraisal property" branch of Client
 * Data · New Client (Action 6). Renders an inline PropertyIntakeForm in
 * CREATE mode so the operator captures the appraisal address + roof
 * intake questions WITHOUT ever leaving the runner.
 *
 * On mount (idempotent, once per action-instance):
 *   1. POSTs a fresh stub property `{client_id}` → new property_id.
 *   2. POSTs a "Main Roof" roof_details row so Action 5 downstream can
 *      just UPDATE (mirrors iter303ag-fix2 behaviour on the auto-created
 *      home property).
 *   3. PATCHes the currently-linked home property's `property_comments`
 *      with a "home address — not this appraisal" note so it's clear on
 *      the client page which property is which.
 *   4. Stashes the new id under `captured_data.new_property_id` so if
 *      the runner remounts we don't create another stub.
 *
 * On Save (inside PropertyIntakeForm):
 *   • PUTs the full form values to the new property + POST/PUTs roof
 *     fields via the existing PropertyIntakeForm plumbing.
 *   • Calls onNext with `{ new_property_id, property_record, form }`.
 *
 * The runner's `advance()` then SWAPS `captured_data.property_id` to
 * `new_property_id` so every downstream action (Tenant, Agent, Roof
 * intake, Urgent Concerns, etc.) targets the new appraisal property.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import PropertyIntakeForm from '../intake/PropertyIntakeForm';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function CreateNewPropertyForm({ action, capturedData, value, onChange, onNext, onBack, onCancel, canBack,
                                                  statusKey, taskId, onTemplateChanged }) {
  const params = action.params || {};
  const clientId = capturedData?.client_id;
  const homePropertyId = capturedData?.property_id;   // the auto-created home
  const preExistingNewId = capturedData?.new_property_id || null;

  const [newPropertyId, setNewPropertyId] = useState(preExistingNewId);
  const [newPropertyRecord, setNewPropertyRecord] = useState(null);
  const [error, setError] = useState('');
  const bootstrapping = useRef(false);
  const flagNote = params.flag_home_note || 'home address — not this appraisal';

  // Bootstrap: create the new stub property + Main Roof + flag old
  // property. Runs ONCE per unique action.id (guarded by ref).
  useEffect(() => {
    if (bootstrapping.current) return;
    if (!clientId) return;
    if (newPropertyId) {
      // Already created earlier (e.g. remount after Back); just refresh
      // the record so PropertyIntakeForm hydrates.
      api.get(`/properties/${newPropertyId}`)
        .then(res => setNewPropertyRecord(res.data))
        .catch(() => {});
      return;
    }
    bootstrapping.current = true;
    (async () => {
      try {
        // 1) Flag the auto-created home property with the note so both
        // records are distinguishable on the client page.
        if (homePropertyId) {
          try {
            // Fetch first to append rather than clobber existing notes.
            const cur = await api.get(`/properties/${homePropertyId}`);
            const existingNote = (cur.data?.property_comments || '').trim();
            const patched = existingNote.includes(flagNote)
              ? existingNote
              : (existingNote ? `${existingNote} · ${flagNote}` : flagNote);
            if (patched !== existingNote) {
              await api.patch(`/properties/${homePropertyId}`,
                { property_comments: patched });
            }
          } catch (e) { /* non-fatal */ }
        }
        // 2) POST the new stub property (blank address; operator fills in).
        const propRes = await api.post('/properties', {
          client_id: clientId,
          property_comments: 'New appraisal property (added via workflow)',
        });
        const nid = propRes.data?._id || propRes.data?.id;
        // 3) Proactively POST a Main Roof roof_details row.
        if (nid) {
          try {
            await api.post('/roof-details', {
              property_id: nid,
              description: 'Main Roof',
            });
          } catch (e) { /* non-fatal — Action 5 will lazily create if needed */ }
        }
        setNewPropertyId(nid);
        setNewPropertyRecord(propRes.data);
        // Persist to the runner state so a Back-then-forward keeps this id.
        onChange({ new_property_id: nid, property_record: propRes.data });
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || 'Failed to create new property');
        bootstrapping.current = false;
      }
    })();
  }, [action.id, clientId]);

  const draftForm = useMemo(() => {
    const forms = capturedData?.property_intake_forms || {};
    return forms[action.id] || {};
  }, [capturedData, action.id]);

  const initialSig = JSON.stringify({ rec: newPropertyRecord, df: draftForm });
  const initial = useMemo(() => ({
    ...(newPropertyRecord || {}),
    ...draftForm,
  }), [initialSig]);

  const autoSaveDraft = (draft) => onChange({
    new_property_id: newPropertyId,
    property_record: newPropertyRecord,
    form: draft,
  });

  const handleSave = (result) => {
    // Emit the shape advance() expects. Extra `new_property_id` key
    // triggers the runner to swap captured_data.property_id.
    const payload = {
      new_property_id: result.property_id,
      property_id: result.property_id,
      property_record: result.property_record,
      property_intake_form: result.form,
    };
    onChange(payload);
    onNext(payload);
  };

  const editCtx = (statusKey && taskId && action?.id) ? {
    statusKey, taskId, actionId: action.id,
    headerTitle: params.header_title,
    headerDescription: params.header_description,
    onTemplateChanged,
  } : null;

  if (error) {
    return (
      <div className="p-4 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800" data-testid="wf-cnp-error">
        {error}
      </div>
    );
  }
  if (!newPropertyId || !newPropertyRecord) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 p-4" data-testid="wf-cnp-bootstrapping">
        <Loader2 size={16} className="animate-spin" /> Preparing new property record…
      </div>
    );
  }

  return (
    <div>
      <PropertyIntakeForm
        propertyId={newPropertyId}
        initial={initial}
        prompts={params.prompts || ''}
        onSave={handleSave}
        onCancel={() => { if (onCancel) return onCancel(); if (canBack) onBack(); }}
        autoSaveDraft={autoSaveDraft}
        editCtx={editCtx}
      />
    </div>
  );
}
